import type { Call, Device } from "@twilio/voice-sdk";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { useOrgMembers } from "@/api/org-member";
import type { Customer } from "@/api/types";
import { voiceService } from "@/api/voice";
import { useAuthStore } from "@/lib/auth";
import { useHeliosWebCallingFeature } from "@/lib/feature-flags/use-feature-flag";
import { CallWidget } from "./call-widget";
import { getVoiceDeviceId } from "./device-id";
import { IncomingCallCard } from "./incoming-call";
import { NewCallDialog } from "./new-call-dialog";

export type VoiceCallStatus = "idle" | "incoming" | "connecting" | "active";

export interface RemoteParty {
  /** Display name when known (e.g. calling from a contact). */
  name?: string;
  phoneNumber: string;
}

interface VoiceContextValue {
  /** PostHog `helios-web-calling` flag state. */
  callingEnabled: boolean;
  status: VoiceCallStatus;
  remote: RemoteParty | null;
  muted: boolean;
  /** Epoch ms when the current call connected; null until answered. */
  startedAt: number | null;
  startCall: (props: { to: string; name?: string }) => Promise<void>;
  /**
   * Open the floating dialer on a confirmation stage instead of dialing
   * immediately. Used by one-click entry points (thread header, contact
   * sidebar) so a stray click never places a call. Pass the customer when
   * available so the confirm stage can show their avatar.
   */
  requestCall: (props: { to: string; name?: string; customer?: Partial<Customer> }) => void;
  acceptIncoming: () => void;
  rejectIncoming: () => void;
  endCall: () => void;
  toggleMute: () => void;
  sendDigits: (digits: string) => void;
  /** Whether the floating "new call" dialer is open. */
  dialerOpen: boolean;
  /** Open the floating dialer (rendered globally, survives navigation). */
  openDialer: () => void;
  closeDialer: () => void;
}

const VoiceContext = createContext<VoiceContextValue | null>(null);

export function useVoice(): VoiceContextValue {
  const context = useContext(VoiceContext);
  if (!context) {
    throw new Error("useVoice must be used within a VoiceProvider");
  }
  return context;
}

/**
 * Terminal SIP outcomes that Twilio surfaces as SDK "errors" when the far
 * end never answers. These are normal call endings, not failures, so we
 * show a neutral message instead of the raw error (e.g. 31005 "Error sent
 * from gateway in HANGUP").
 */
const CALL_END_MESSAGES: Record<number, string> = {
  31002: "Call declined",
  31005: "Call ended",
  31408: "No answer",
  31480: "No answer",
  31486: "The line is busy",
  31600: "Call could not be completed",
  31603: "Call declined",
};

function notifyCallError(error: { message?: string; code?: number } | undefined) {
  const friendly = error?.code != null ? CALL_END_MESSAGES[error.code] : undefined;
  if (friendly) {
    toast.info(friendly);
  } else {
    toast.error(error?.message ?? "Call failed");
  }
}

type VoiceSdk = typeof import("@twilio/voice-sdk");

let sdkPromise: Promise<VoiceSdk> | null = null;

function loadVoiceSdk(): Promise<VoiceSdk> {
  if (!sdkPromise) {
    sdkPromise = import("@twilio/voice-sdk");
  }
  return sdkPromise;
}

export function VoiceProvider({ children }: { children: ReactNode }) {
  const { enabled: callingEnabled } = useHeliosWebCallingFeature();
  const user = useAuthStore((state) => state.user);
  const org = useAuthStore((state) => state.org);
  const gym = useAuthStore((state) => state.gym);

  const [status, setStatus] = useState<VoiceCallStatus>("idle");
  const [remote, setRemote] = useState<RemoteParty | null>(null);
  const [muted, setMuted] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [dialerOpen, setDialerOpen] = useState(false);
  const [pendingCall, setPendingCall] = useState<{
    to: string;
    name?: string;
    customer?: Partial<Customer>;
  } | null>(null);

  const deviceRef = useRef<Device | null>(null);
  const deviceOrgRef = useRef<string | null>(null);
  const callRef = useRef<Call | null>(null);
  const incomingRef = useRef<Call | null>(null);
  const orgIdRef = useRef<string | null>(null);
  orgIdRef.current = org?.id ?? null;
  const gymIdRef = useRef<string | null>(null);
  gymIdRef.current = gym?.id ?? null;

  const { data: members } = useOrgMembers(
    org?.id ?? "",
    {},
    { skip: !callingEnabled || !org?.id || !user?.id },
  );
  const member = members?.find((doc) => doc.user.id === user?.id);
  const shouldReceiveCalls =
    callingEnabled === true &&
    (member?.preferences?.voice?.some((preference) => preference.incoming === true) ?? false);

  const resetCallState = useCallback(() => {
    callRef.current = null;
    incomingRef.current = null;
    setStatus("idle");
    setRemote(null);
    setMuted(false);
    setStartedAt(null);
  }, []);

  const attachCallLifecycle = useCallback(
    (call: Call) => {
      call.on("accept", () => {
        setStatus("active");
        setStartedAt(Date.now());
        setMuted(call.isMuted());
      });
      call.on("mute", (isMuted: boolean) => setMuted(isMuted));
      call.on("disconnect", resetCallState);
      call.on("cancel", resetCallState);
      call.on("reject", resetCallState);
      call.on("error", (error: { message?: string; code?: number }) => {
        notifyCallError(error);
        resetCallState();
      });
    },
    [resetCallState],
  );

  const fetchToken = useCallback(async () => {
    const orgId = orgIdRef.current;
    if (!orgId) {
      throw new Error("No active organization");
    }
    return voiceService.createAccessToken({
      org: orgId,
      device: getVoiceDeviceId(),
      incomingAllow: true,
    });
  }, []);

  const destroyDevice = useCallback(() => {
    deviceRef.current?.destroy();
    deviceRef.current = null;
    deviceOrgRef.current = null;
  }, []);

  const handleIncoming = useCallback(
    (call: Call) => {
      incomingRef.current = call;
      setStatus("incoming");
      setRemote({ phoneNumber: call.parameters.From ?? "" });
      attachCallLifecycle(call);
    },
    [attachCallLifecycle],
  );

  const handleIncomingRef = useRef(handleIncoming);
  handleIncomingRef.current = handleIncoming;
  const fetchTokenRef = useRef(fetchToken);
  fetchTokenRef.current = fetchToken;

  const ensureDevice = useCallback(async (): Promise<Device> => {
    const orgId = orgIdRef.current;
    if (!orgId) {
      throw new Error("No active organization");
    }

    if (deviceRef.current && deviceOrgRef.current === orgId) {
      return deviceRef.current;
    }

    destroyDevice();

    const [sdk, accessToken] = await Promise.all([loadVoiceSdk(), fetchTokenRef.current()]);

    // A concurrent ensureDevice call may have created the device while we
    // were fetching the token.
    if (deviceRef.current && deviceOrgRef.current === orgId) {
      return deviceRef.current;
    }

    const device = new sdk.Device(accessToken.token, {
      codecPreferences: [sdk.Call.Codec.Opus, sdk.Call.Codec.PCMU],
      logLevel: "error",
      // Maps generic gateway hangups (31005) to precise SIP outcomes such
      // as 31480 no-answer / 31486 busy / 31603 declined.
      enableImprovedSignalingErrorPrecision: true,
    });

    device.on("incoming", (call: Call) => handleIncomingRef.current(call));
    device.on("tokenWillExpire", async () => {
      try {
        const refreshed = await fetchTokenRef.current();
        device.updateToken(refreshed.token);
      } catch {
        // Next register/connect attempt will surface the failure.
      }
    });
    device.on("error", (error: { message?: string; code?: number }) => {
      // AccessTokenExpired and similar recoverable errors are retried by
      // the SDK; only surface errors while the user is in a call flow.
      if (callRef.current || incomingRef.current) {
        notifyCallError(error);
      }
    });

    deviceRef.current = device;
    deviceOrgRef.current = orgId;
    return device;
  }, [destroyDevice]);

  // Register for incoming calls when the member opted in for at least one
  // gym; tear the device down when calling is disabled (unless mid-call).
  useEffect(() => {
    if (!shouldReceiveCalls) {
      if (!callRef.current && !incomingRef.current) {
        destroyDevice();
      }
      return;
    }

    let cancelled = false;
    ensureDevice()
      .then((device) => {
        if (!cancelled && device.state === "unregistered") {
          return device.register();
        }
      })
      .catch(() => {
        // Registration is best-effort; outbound calls create the device
        // on demand and will surface their own errors.
      });

    return () => {
      cancelled = true;
    };
  }, [shouldReceiveCalls, ensureDevice, destroyDevice]);

  // Recreate the device when the active org changes (tokens are per-org).
  const orgId = org?.id ?? null;
  useEffect(() => {
    if (deviceRef.current && deviceOrgRef.current !== orgId && !callRef.current) {
      destroyDevice();
    }
  }, [orgId, destroyDevice]);

  useEffect(() => destroyDevice, [destroyDevice]);

  const startCall = useCallback(
    async ({ to, name }: { to: string; name?: string }) => {
      if (callRef.current || incomingRef.current) {
        toast.error("You are already on a call");
        return;
      }
      const gymId = gymIdRef.current;
      if (!gymId) {
        toast.error("Select an inbox before placing a call");
        return;
      }

      setStatus("connecting");
      setRemote({ name, phoneNumber: to });

      try {
        const device = await ensureDevice();
        const call = await device.connect({ params: { to, gym: gymId } });
        callRef.current = call;
        attachCallLifecycle(call);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Could not start the call");
        resetCallState();
      }
    },
    [ensureDevice, attachCallLifecycle, resetCallState],
  );

  const acceptIncoming = useCallback(() => {
    const call = incomingRef.current;
    if (!call) return;
    callRef.current = call;
    incomingRef.current = null;
    setStatus("connecting");
    call.accept();
  }, []);

  const rejectIncoming = useCallback(() => {
    incomingRef.current?.reject();
    resetCallState();
  }, [resetCallState]);

  const endCall = useCallback(() => {
    callRef.current?.disconnect();
    resetCallState();
  }, [resetCallState]);

  const toggleMute = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    call.mute(!call.isMuted());
  }, []);

  const sendDigits = useCallback((digits: string) => {
    callRef.current?.sendDigits(digits);
  }, []);

  const openDialer = useCallback(() => setDialerOpen(true), []);
  const closeDialer = useCallback(() => {
    setDialerOpen(false);
    setPendingCall(null);
  }, []);

  const requestCall = useCallback(
    ({ to, name, customer }: { to: string; name?: string; customer?: Partial<Customer> }) => {
      setPendingCall({ to, name, customer });
      setDialerOpen(true);
    },
    [],
  );

  const handleDialerOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setDialerOpen(true);
      } else {
        closeDialer();
      }
    },
    [closeDialer],
  );

  const value = useMemo<VoiceContextValue>(
    () => ({
      callingEnabled,
      status,
      remote,
      muted,
      startedAt,
      startCall,
      requestCall,
      acceptIncoming,
      rejectIncoming,
      endCall,
      toggleMute,
      sendDigits,
      dialerOpen,
      openDialer,
      closeDialer,
    }),
    [
      callingEnabled,
      status,
      remote,
      muted,
      startedAt,
      startCall,
      requestCall,
      acceptIncoming,
      rejectIncoming,
      endCall,
      toggleMute,
      sendDigits,
      dialerOpen,
      openDialer,
      closeDialer,
    ],
  );

  return (
    <VoiceContext.Provider value={value}>
      {children}
      {status === "incoming" && <IncomingCallCard />}
      {(status === "connecting" || status === "active") && <CallWidget />}
      {/* Rendered here (not per page) so the dialer survives navigation. */}
      <NewCallDialog
        open={dialerOpen}
        onOpenChange={handleDialerOpenChange}
        pendingCall={pendingCall}
      />
    </VoiceContext.Provider>
  );
}
